import { useEffect, useMemo, useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import MilesTracker from '@src/components/MilesTracker'
import ActiveBoxIcon from '@src/components/icons/ActiveBox'
import BoxIcon from '@src/components/icons/Box'
import CheckIcon from '@src/components/icons/Check'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import CloseIcon from '@src/components/icons/Close'
import ExchangeIcon from '@src/components/icons/Exchange'
import InfoIcon from '@src/components/icons/Info'
import LoadingIcon from '@src/components/icons/Loading'
import ProcessIcon from '@src/components/icons/Process'
import SuccessIcon from '@src/components/icons/Success'
import TimerIcon from '@src/components/icons/Timer'
import { CCTP_TOOLTIP } from '@src/constants/exchange'
import { ResultCode, StepNames } from '@src/enums'
import { LiquiditySource } from '@src/enums'
import { SourceTxComponent } from '@src/pages/Exchange/components/SwapInProgressFlow/SourceTxComponent'
import TransactionDetails from '@src/pages/Exchange/components/SwapInProgressFlow/TransactionDetails'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { IFee } from '@src/types/fee'
import { isFeatureEnabled } from '@src/utils/environment'
import { useConfigStore } from '@src/zustand_stores'

interface IProcessingSwapProps {
    onClose: () => void
    currentStep: StepNames
    fees: IFee[]
    destinationAddress?: string
    miles?: number
    result?: {
        transactionETA?: number
        status: ResultCode
        sourceTxHash?: string
        destinationTxHash?: string
        message?: string
    }
    fullTradeTimeTimer: any
    liquiditySource: LiquiditySource
    isCCTPTransfer: boolean
}
export default function ProcessingSwap({
    onClose,
    currentStep,
    miles,
    fees,
    destinationAddress,
    result = undefined,
    fullTradeTimeTimer,
    liquiditySource,
    isCCTPTransfer,
}: IProcessingSwapProps) {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const [showDetails, setShowDetails] = useState(false)
    const [showTestnetTooltip, setShowTestnetTooltip] = useState(false)
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const { sellData, updateSellData, buyData, updateBuyData } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    useEffect(() => {
        if (result && result.status === ResultCode.success) {
            updateSellData({ ...sellData, value: BigInt(0) })
            updateBuyData({ ...buyData, value: BigInt(0) })
        }
    }, [result?.status])

    const transactionHash = useMemo(() => {
        if (result?.sourceTxHash) {
            const urlParts = result.sourceTxHash.split('/')
            return urlParts[urlParts.length - 1]
        }
        return ''
    }, [result?.sourceTxHash])

    return (
        <div className='space-y-10 px-4'>
            <div className='flex w-full items-center'>
                <div className='flex-1' />
                <p className='flex-10 text-2xl font-medium'>Processing Swap</p>
                <div className='flex flex-1 justify-end'>
                    <CloseIcon onClick={onClose} className='h-[18px] w-[18px] cursor-pointer text-mach-gray' />
                </div>
            </div>
            <div className='relative flex h-[212px] items-center justify-between rounded-3xl border border-[#1A1B20] px-8'>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={sellData.token?.logoURI}
                        networkIcon={availableNetworks[sellData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
                <div className='absolute left-1/2 top-0 flex h-full -translate-x-1/2 items-center justify-between gap-2'>
                    <ProcessIcon isLeftIcon={true} />
                    <div className='flex h-5 w-5 items-center justify-center rounded-full bg-[#16171A]'>
                        <ExchangeIcon />
                    </div>
                    <ProcessIcon />
                </div>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={buyData.token?.logoURI}
                        networkIcon={availableNetworks[buyData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
            </div>
            <div className='space-y-6'>
                {currentStep === StepNames.SWITCH_NETWORKS && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Switching Network</span>
                        </div>
                        <span className='text-[13px]'>
                            Switch your network to {availableNetworks[sellData.chainId].name} in your wallet.{' '}
                        </span>
                    </div>
                )}
                {currentStep === StepNames.DEPOSIT_WETH && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#34C7591A] px-3 py-2 text-[#34C759]'>
                        <div className='flex items-center gap-2'>
                            <SuccessIcon />
                            <span className='text-sm font-medium'>Wrapping gas</span>
                        </div>
                        <span className='text-[13px]'>Wrap the gas tokens in your wallet.</span>
                    </div>
                )}
                {currentStep === StepNames.DEPOSIT_WETH_PENDING && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Wrapping gas</span>
                        </div>
                        <span className='text-[13px]'>Waiting for confirmation. </span>
                    </div>
                )}
                {currentStep === StepNames.APPROVE_TOKENS && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#34C7591A] px-3 py-2 text-[#34C759]'>
                        <div className='flex items-center gap-2'>
                            <SuccessIcon />
                            <span className='text-sm font-medium'>Approving token</span>
                        </div>
                        <span className='text-[13px]'>Approve the signature in your wallet.</span>
                    </div>
                )}
                {currentStep === StepNames.APPROVE_TOKENS_PENDING && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Approving token</span>
                        </div>
                        <span className='text-[13px]'>Waiting for confirmation. </span>
                    </div>
                )}
                {currentStep === StepNames.CONFIRM_SWAP && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#34C7591A] px-3 py-2 text-[#34C759]'>
                        <div className='flex items-center gap-2'>
                            <SuccessIcon />
                            <span className='text-sm font-medium'>Confirm Swap</span>
                        </div>
                        <span className='text-[13px]'>Confirm the swap in your wallet.</span>
                    </div>
                )}
                {currentStep === StepNames.CONFIRM_SWAP_PENDING && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Confirming Swap</span>
                        </div>
                        <span className='text-[13px]'>Waiting for confirmation. </span>
                    </div>
                )}
                {[StepNames.MATCHING, StepNames.MATCH_CREATED, StepNames.MATCH_EXECUTED].includes(currentStep) &&
                    result?.status === ResultCode.pending && (
                        <div className='flex flex-col gap-4'>
                            {/* Timer section */}
                            <div className='flex items-center justify-center gap-2'>
                                <div className='flex items-center gap-1'>
                                    <TimerIcon />
                                </div>
                                <span className='text-sm font-medium'>
                                    {fullTradeTimeTimer.hours}h {fullTradeTimeTimer.minutes}m{' '}
                                    {fullTradeTimeTimer.seconds}s
                                </span>
                            </div>

                            {/* Progress steps */}
                            <div className='flex gap-2'>
                                <div className='flex flex-col items-center'>
                                    <div className='flex h-8 w-8 items-center justify-center'>
                                        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#16171A] shadow-[0_0_2px_#349FFD]'>
                                            {currentStep === StepNames.MATCHING ||
                                            currentStep === StepNames.MATCH_CREATED ||
                                            currentStep === StepNames.MATCH_EXECUTED ? (
                                                <ActiveBoxIcon />
                                            ) : (
                                                <BoxIcon />
                                            )}
                                        </div>
                                    </div>
                                    <div className='h-12 w-[2px] bg-[#16171A]' />
                                    <div className='flex h-8 w-8 items-center justify-center'>
                                        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#16171A]'>
                                            {currentStep === StepNames.MATCH_CREATED ||
                                            currentStep === StepNames.MATCH_EXECUTED ? (
                                                <ActiveBoxIcon />
                                            ) : (
                                                <BoxIcon />
                                            )}
                                        </div>
                                    </div>
                                    <div className='h-12 w-[2px] bg-[#16171A]' />
                                    <div className='flex h-8 w-8 items-center justify-center'>
                                        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-[#16171A]'>
                                            <BoxIcon />
                                        </div>
                                    </div>
                                </div>
                                <div className='flex flex-grow flex-col justify-between py-2'>
                                    <div className='flex items-center justify-between'>
                                        <span className='text-sm'>Placed order</span>
                                        {currentStep === StepNames.MATCHING ? (
                                            <LoadingIcon className='animate-spin' />
                                        ) : currentStep == StepNames.MATCH_CREATED ||
                                          currentStep == StepNames.MATCH_EXECUTED ? (
                                            <CheckIcon width={20} height={20} />
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                    <div className='flex items-center justify-between text-sm'>
                                        <span>Match Created</span>
                                        {currentStep === StepNames.MATCH_CREATED ? (
                                            <LoadingIcon className='animate-spin' />
                                        ) : currentStep == StepNames.MATCH_EXECUTED ? (
                                            <CheckIcon width={20} height={20} />
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                    <div className='flex items-center justify-between text-sm'>
                                        <span>Match Executed</span>
                                        {currentStep === StepNames.MATCH_EXECUTED ? (
                                            result?.status === ResultCode.pending ? (
                                                <LoadingIcon className='animate-spin' />
                                            ) : (
                                                <CheckIcon width={20} height={20} />
                                            )
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* CCTP Information Banner */}
                            {isCCTPTransfer && (
                                <div className='mt-2 flex flex-col gap-3 rounded-xl border border-blue-500/20 bg-blue-500/5 p-4'>
                                    <div className='flex items-center gap-2'>
                                        <div className='flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/10'>
                                            <LoadingIcon className='h-4 w-4 animate-spin text-blue-400' />
                                        </div>
                                        <span className='text-sm font-medium text-blue-400'>
                                            CCTP Transfer in Progress
                                        </span>
                                    </div>
                                    <div className='flex items-start gap-2 text-[13px] leading-5 text-mach-sub-text'>
                                        <span>{CCTP_TOOLTIP}</span>
                                    </div>
                                    <div className='flex items-center gap-2 rounded-lg bg-blue-500/10 px-3 py-2'>
                                        <TimerIcon className='h-4 w-4 text-blue-400' />
                                        <span className='text-xs text-blue-400'>
                                            Estimated completion: 5-20 minutes
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                <div className='flex items-center gap-4'>
                    <div className='flex-grow border border-[#313339]'></div>
                    <div
                        className='flex cursor-pointer items-center gap-1'
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        <span className='text-sm text-mach-accent-forground'>Details</span>
                        <ChevronDownIcon className={cn('h-4 w-4 transition-all', !showDetails ? 'rotate-180' : '')} />
                    </div>
                    <div className='flex-grow border border-[#313339]'></div>
                </div>
                {
                    <div
                        className={cn(
                            'grid transition-all duration-500',
                            showDetails ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                        )}
                    >
                        <div className='overflow-hidden'>
                            <div className='space-y-6'>
                                <SourceTxComponent sourceTxHash={result?.sourceTxHash}></SourceTxComponent>
                                {!!miles && !isFeatureEnabled('USE_TESTNETS_ONLY') && <MilesTracker miles={miles} />}
                                <TransactionDetails
                                    sellData={sellData}
                                    buyData={buyData}
                                    fees={fees}
                                    destinationAddress={destinationAddress}
                                    transactionHash={transactionHash}
                                    currentStep={currentStep}
                                    liquiditySource={liquiditySource}
                                    // do not show this here (dup info w/ the rest of the flow)
                                    isCCTPTransfer={false}
                                />
                            </div>
                        </div>
                    </div>
                }

                {/* Testnet warning tooltip */}
                {isTestnet && (
                    <div className='relative mt-4 flex items-center justify-center'>
                        <div
                            className='flex cursor-pointer items-center gap-2 rounded-lg bg-mach-button-tabs-background px-4 py-2'
                            onMouseEnter={() => setShowTestnetTooltip(true)}
                            onMouseLeave={() => setShowTestnetTooltip(false)}
                        >
                            <InfoIcon className='h-4 w-4 text-mach-sub-text' />
                            <span className='text-sm text-mach-sub-text'>Testnet Information</span>
                            {showTestnetTooltip && (
                                <div className='absolute bottom-full left-1/2 mb-2 w-80 -translate-x-1/2 rounded-md bg-mach-modal-background p-3 text-xs text-white shadow-lg'>
                                    Testnet transactions may struggle to confirm as executed on this page. Please check
                                    your swap's status on the Orders page before resubmitting.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
